import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, updateUserProfile } from '../services/userService';
import { User, Check, X, Envelope, Phone, MapPin } from 'phosphor-react';

function MyProfile() {
  // Hardcode to Dutch locale
  useEffect(() => {
    i18n.changeLanguage('nl');
  }, []);

  const { t } = useTranslation();
  const { user, updatePassword } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street_and_housenumber: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'Belgium'
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const profileData = await getUserProfile(user.id);
        setProfile(profileData);
        setFormData({
          first_name: profileData.first_name || '',
          last_name: profileData.last_name || '',
          email: profileData.email || '',
          phone: profileData.phone || '',
          street_and_housenumber: profileData.street_and_housenumber || '',
          city: profileData.city || '',
          state_province: profileData.state_province || '',
          postal_code: profileData.postal_code || '',
          country: profileData.country || 'Belgium'
        });
      } catch (err) {
        setError(t('common.error'));
        console.error('Error loading profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user, t]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (section) => {
    if (!user) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await updateUserProfile(user.id, formData);
      
      // Refresh profile data
      const updatedProfile = await getUserProfile(user.id);
      setProfile(updatedProfile);
      
      setEditingSection(null);
      setSuccess(t('profile.profileUpdated'));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('common.error'));
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('profile.passwordMismatch'));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError(t('profile.passwordTooShort'));
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await updatePassword(passwordData.newPassword);
      
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordForm(false);
      setSuccess(t('profile.passwordUpdated'));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(t('common.error'));
      console.error('Error updating password:', err);
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditingSection(null);
    // Reset form data to original profile data
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        street_and_housenumber: profile.street_and_housenumber || '',
        city: profile.city || '',
        state_province: profile.state_province || '',
        postal_code: profile.postal_code || '',
        country: profile.country || 'Belgium'
      });
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <User size={64} className="text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('profile.pleaseSignIn')}</h2>
            <p className="text-gray-600 mb-6">{t('profile.signInRequired')}</p>
            <button className="bg-ambre-600 text-white px-6 py-2 rounded-lg hover:bg-ambre-700 transition">
              {t('nav.login')}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ambre-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('profile.loading')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <div className="flex-1 py-16 px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('profile.title')}</h1>
            <p className="text-gray-600">{t('profile.subtitle')}</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-600">{success}</p>
            </div>
          )}

          <div className="space-y-8">
            {/* Personal Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-ambre-100 rounded-lg flex items-center justify-center">
                    <User size={20} className="text-ambre-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">{t('profile.personalInfo')}</h2>
                </div>
                
                {editingSection !== 'personal' ? (
                  <button
                    onClick={() => setEditingSection('personal')}
                    className="text-ambre-600 hover:text-ambre-700 font-medium"
                  >
                    {t('profile.edit')}
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleSave('personal')}
                      disabled={saving}
                      className="flex items-center space-x-1 bg-ambre-600 text-white px-4 py-2 rounded-lg hover:bg-ambre-700 transition disabled:opacity-50"
                    >
                      <Check size={16} />
                      <span>{t('profile.save')}</span>
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="flex items-center space-x-1 text-gray-600 hover:text-gray-700"
                    >
                      <X size={16} />
                      <span>{t('profile.cancel')}</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.firstName')}</label>
                  {editingSection === 'personal' ? (
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                    />
                  ) : (
                    <p className="text-gray-900">{profile?.first_name || t('profile.notProvided')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.lastName')}</label>
                  {editingSection === 'personal' ? (
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                    />
                  ) : (
                    <p className="text-gray-900">{profile?.last_name || t('profile.notProvided')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.email')}</label>
                  <div className="flex items-center space-x-2">
                    <Envelope size={16} className="text-gray-400" />
                    <p className="text-gray-900">{profile?.email}</p>
                    {profile?.email_verified && (
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">{t('profile.verified')}</span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.phone')}</label>
                  {editingSection === 'personal' ? (
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Phone size={16} className="text-gray-400" />
                      <p className="text-gray-900">{profile?.phone || t('profile.notProvided')}</p>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Address Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-ambre-100 rounded-lg flex items-center justify-center">
                    <MapPin size={20} className="text-ambre-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">{t('profile.addressInfo')}</h2>
                </div>
                
                {editingSection !== 'address' ? (
                  <button
                    onClick={() => setEditingSection('address')}
                    className="text-ambre-600 hover:text-ambre-700 font-medium"
                  >
                    {t('profile.edit')}
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleSave('address')}
                      disabled={saving}
                      className="flex items-center space-x-1 bg-ambre-600 text-white px-4 py-2 rounded-lg hover:bg-ambre-700 transition disabled:opacity-50"
                    >
                      <Check size={16} />
                      <span>{t('profile.save')}</span>
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="flex items-center space-x-1 text-gray-600 hover:text-gray-700"
                    >
                      <X size={16} />
                      <span>{t('profile.cancel')}</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.streetAndHousenumber')}</label>
                  {editingSection === 'address' ? (
                    <input
                      type="text"
                      name="street_and_housenumber"
                      value={formData.street_and_housenumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                    />
                  ) : (
                    <p className="text-gray-900">{profile?.street_and_housenumber || t('profile.notProvided')}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.city')}</label>
                    {editingSection === 'address' ? (
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                      />
                    ) : (
                      <p className="text-gray-900">{profile?.city || t('profile.notProvided')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.postalCode')}</label>
                    {editingSection === 'address' ? (
                      <input
                        type="text"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                      />
                    ) : (
                      <p className="text-gray-900">{profile?.postal_code || t('profile.notProvided')}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.country')}</label>
                    {editingSection === 'address' ? (
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                      >
                        <option value="Belgium">Belgium</option>
                        <option value="France">France</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="Germany">Germany</option>
                        <option value="Luxembourg">Luxembourg</option>
                      </select>
                    ) : (
                      <p className="text-gray-900">{profile?.country || t('profile.notProvided')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Password */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">{t('profile.password')}</h2>
                
                {!showPasswordForm && (
                  <button
                    onClick={() => setShowPasswordForm(true)}
                    className="text-ambre-600 hover:text-ambre-700 font-medium"
                  >
                    {t('profile.changePassword')}
                  </button>
                )}
              </div>

              {showPasswordForm ? (
                <form onSubmit={handlePasswordUpdate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.newPassword')}</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.confirmPassword')}</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ambre-500 focus:border-ambre-500"
                    />
                  </div>

                  <div className="flex space-x-4">
                    <button
                      type="submit"
                      disabled={saving}
                      className="bg-ambre-600 text-white px-6 py-3 rounded-lg hover:bg-ambre-700 transition disabled:opacity-50"
                    >
                      {t('profile.updatePassword')}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                      }}
                      className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition"
                    >
                      {t('profile.cancel')}
                    </button>
                  </div>
                </form>
              ) : (
                <p className="text-gray-600">{t('profile.passwordDescription')}</p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default MyProfile; 